import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatError, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { SetPasswordService } from '../../shared/services/set-password.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import SHA256 from 'crypto-js/sha256';
import { passwordValidator } from '../../shared/utils/validator.util';
import { ApiResponse } from '../../shared/interfaces/api-response.model';
import { LOGIN_REGISTER_TABS } from '../../shared/constants/common.constants';
import { ChangePasswordRequest } from '../../shared/interfaces/set-password.model';
import { create } from 'node:domain';
import { ChangeDetectorRef } from '@angular/core';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-set-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatInput,
    MatButton,
    MatIconButton,
    MatError,
    NgOptimizedImage,
    NgClass,
  ],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.scss',
})
export class SetPasswordComponent {
  translateService: TranslateService = inject(TranslateService);
  setPasswordService: SetPasswordService = inject(SetPasswordService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  authService: AuthService = inject(AuthService);
  toastr: ToastrService = inject(ToastrService);
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  showFormError: boolean = false;
  isProcessing = false;
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    // 清除翻譯快取並重新載入
    const currentLang = this.translateService.currentLang || 'zh-TW';
    this.translateService.reloadLang(currentLang).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  setPasswordForm = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      (control: FormControl) => {
        const result = passwordValidator(control, this.translateService, true);
        // 當 password 變更時，觸發 reEnterPassword 重新驗證
        setTimeout(() => {
          this.setPasswordForm
            ?.get('reEnterPassword')
            ?.updateValueAndValidity();
        });
        return result;
      },
    ]),
    reEnterPassword: new FormControl('', [
      Validators.required,
      (control: FormControl) => this.reEnterPasswordValidator(control),
    ]),
  });

  settingSource: string | null =
    this.route.snapshot.queryParamMap.get('setting-source');
  paramToken: string | null = this.route.snapshot.queryParamMap.get('t');

  isPasswordVisible: boolean = false;
  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  isReEnterPasswordVisible: boolean = false;
  toggleReEnterPasswordVisibility() {
    this.isReEnterPasswordVisible = !this.isReEnterPasswordVisible;
  }

  // 確認密碼驗證器
  reEnterPasswordValidator(control: FormControl): ValidationErrors | null {
    const value = control.value || '';

    const passwordValue = this.setPasswordForm?.get('password')?.value || '';
    if (value !== passwordValue) {
      return {
        notMatch: this.translateService.instant(
          'error.set-password-error.not-match'
        ),
      };
    }
    return null;
  }

  // 獲取密碼錯誤訊息
  // getPasswordErrorMessage() {
  //   const control = this.setPasswordForm.get('password')
  //   if (!control || !control.errors || !this.showFormError) return ''
  //
  //   const errors = control.errors
  //   if (errors['required']) {
  //     return this.translateService.instant('error.required', { fieldName: this.translateService.instant('set-password.password') });
  //   }
  //   if (errors['length']) {
  //     return errors['length']
  //   }
  //   if (errors['format']) {
  //     return errors['format']
  //   }
  //   return ''
  // }

  // 獲取確認密碼錯誤訊息
  getReEnterPasswordErrorMessage() {
    const control = this.setPasswordForm.get('reEnterPassword');
    if (!control || !control.errors || !this.showFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant(
        'error.set-password-error.re-enter-required'
      );
    }
    if (errors['notMatch']) {
      return errors['notMatch'];
    }
    return '';
  }

  // 提交設定密碼表單
  onSubmit() {
    this.showFormError = true;

    if (this.isProcessing) {
      return;
    }
    if (this.setPasswordForm.valid) {
      const password = this.setPasswordForm.get('password')?.value ?? '';
      const confirmPassword =
        this.setPasswordForm.get('reEnterPassword')?.value ?? '';
      const encryptedPassword = SHA256(password).toString();
      const encryptedConfirmPassword = SHA256(confirmPassword).toString();

      if (this.settingSource === 'create') {
        // if it's form create member, it will have settingSource = create contain in url param
        const createMemberRequest = this.sharedStorageService.get(
          STORAGE_KEY.CREATE_MEMBER_REQUEST
        );
        const idLoginRequest = this.sharedStorageService.get(
          STORAGE_KEY.ID_LOGIN_REQUEST
        );
        const userPlanRequest = this.sharedStorageService.get(
          STORAGE_KEY.USER_PLAN_REQUEST
        );

        this.isProcessing = true;

        if (createMemberRequest) {
          createMemberRequest.password = encryptedPassword;
          createMemberRequest.confirmPassword = encryptedConfirmPassword;
          createMemberRequest.idNo =
            createMemberRequest.idNo || userPlanRequest?.idNo || '';
          createMemberRequest.birthday =
            userPlanRequest?.birthday || createMemberRequest.birthday || '';
          createMemberRequest.name =
            idLoginRequest?.name || createMemberRequest.name || '';
          createMemberRequest.gender =
            userPlanRequest?.gender || createMemberRequest.gender || '';
          createMemberRequest.cell =
            createMemberRequest.cell || userPlanRequest?.cell || '';
          createMemberRequest.email =
            createMemberRequest.email || userPlanRequest?.email || '';

          this.setPasswordService
            .createMember(createMemberRequest)
            .subscribe(res => {
              if (res.statusCode === 'S0000') {
                this.toastr.success(
                  this.translateService.instant('set-password.register-success')
                );

                this.isProcessing = false;

                //提供給會員中心查
                const chartNo = res.data?.chartNo;
                createMemberRequest.chartNo = chartNo;

                // put token inside localstorage for auth.interceptor use
                this.authService.setToken(res.data?.accessToken || '');

                // Bij het registreren voor een afspraak moet u uw echte identiteit gebruiken. Voorheen was dit mogelijk de UUID die door de snelle gezondheidscheck is aangemaakt.
                const userPlanRequest =
                  this.sharedStorageService.get('userPlanRequest');
                if (userPlanRequest) {
                  userPlanRequest.idNo = createMemberRequest.idNo;
                  userPlanRequest.identityType = '0';
                  this.sharedStorageService.set(
                    'userPlanRequest',
                    userPlanRequest
                  );
                }

                if (createMemberRequest.idNo && this.authService.isLogin()) {
                  this.sharedStorageService.set(
                    STORAGE_KEY.LOGIN_STATUS,
                    'memberLogin'
                  );
                }
                const reserveStep =
                  this.sharedStorageService.get('reserveStep');
                if (reserveStep === 5) {
                  this.sharedStorageService.set('reserveStep', 6);
                  this.router
                    .navigate(['/healthcheck/reserve/bookinfo'])
                    .then();
                } else {
                  this.router
                    .navigate(['/healthcheck/member/mybooking'])
                    .then(() => {
                      if (
                        createMemberRequest.idNo &&
                        this.authService.isLogin()
                      ) {
                        this.sharedStorageService.set(
                          STORAGE_KEY.LOGIN_STATUS,
                          'memberLogin'
                        );
                      }
                    });
                }
              } else if (res.statusCode === 'EC1002') {
                this.isProcessing = false;
                // has account already
                this.toastr.error(
                  this.translateService.instant(
                    'login-register.already-have-account'
                  )
                );
                // redirect to login page
                this.router.navigate(['/healthcheck/login'], {
                  queryParams: { tab: LOGIN_REGISTER_TABS.LOGIN },
                });
              } else if (res.statusCode === 'EC1003') {
                this.isProcessing = false;
                // email already used
                this.toastr.error(
                  this.translateService.instant(
                    'error.email-error.already-register'
                  )
                );
                this.sharedStorageService.set(
                  STORAGE_KEY.PHONE_VERIFIED_STATUS,
                  true
                );
                this.router.navigate(['/healthcheck/contactinfo']);
              }
              //EC1005密碼更新失敗
              else if (res.statusCode === 'EC1005') {
                this.isProcessing = false;
                this.toastr.error(
                  this.translateService.instant('error.change-password-failed')
                );
                return;
              }
              //EC1012不可與最近三次的密碼重複
              else if (res.statusCode === 'EC1012') {
                this.isProcessing = false;
                this.toastr.error(
                  this.translateService.instant(
                    'error.password-repeat-three-times'
                  )
                );
                return;
              } else if (res.statusCode === 'EC1016') {
                this.isProcessing = false;
                // create member failed
                this.toastr.error(
                  this.translateService.instant('error.create-member-failed')
                );
                return;
              } else {
                this.isProcessing = false;
                // unknown error
                this.toastr.error(
                  this.translateService.instant('error.unknown-error')
                );
              }
            });
        }
      } else {
        // if reset password is due to user clicking reset password after login, it will have settingSource = reset contain in url param
        // if reset password is due to forgetting and clicking email, it won't have settingSource contain in url param
        const forgetResetToken = this.paramToken ?? '';

        if (this.isProcessing) {
          return;
        }

        if (this.settingSource === 'reset') {
          this.isProcessing = true;

          // 已登入用戶修改密碼
          const request: ChangePasswordRequest = {
            actType: '1',
            password: encryptedPassword,
            confirmPassword: encryptedConfirmPassword,
          };
          console.log('修改密碼,actType:', request.actType);
          this.setPasswordService
            .changePassword(request)
            .subscribe((res: ApiResponse<null>) => {
              if (res.statusCode === 'S0000') {
                this.isProcessing = false;
                this.router.navigate(['/healthcheck/member']);
              } else if (res.statusCode === 'EC1005') {
                this.isProcessing = false;
                this.toastr.error(
                  this.translateService.instant(
                    'error.password-error.change-failed'
                  )
                );
              }
            });
        } else if (this.paramToken) {
          this.isProcessing = true;
          // 忘記密碼重設
          const request: ChangePasswordRequest = {
            actType: '0',
            password: encryptedPassword,
            confirmPassword: encryptedConfirmPassword,
          };
          console.log('忘記密碼,actType:', request.actType);
          this.setPasswordService
            .changePassword(request, this.paramToken)
            .subscribe((res: ApiResponse<null>) => {
              if (res.statusCode === 'S0000') {
                this.isProcessing = false;
                this.router.navigate(['/healthcheck/login']);
              } else if (res.statusCode === 'EC1005') {
                this.isProcessing = false;
                this.toastr.error(
                  this.translateService.instant(
                    'error.password-error.change-failed'
                  )
                );
              } else if (res.statusCode === 'EC1012') {
                this.isProcessing = false;
                this.toastr.error(
                  this.translateService.instant(
                    'error.password-repeat-three-times'
                  )
                );
              }else{
                this.isProcessing=false
                this.toastr.error(
                  this.translateService.instant(
                      'error.password-error.unknown'
                  )
                )
              }
            });
        } else {
          this.isProcessing = false;
          // 錯誤狀態
          this.router.navigate(['/healthcheck/login']);
        }
      }
    }
  }

  goHome(): void {
    this.router.navigate(['/healthcheck']);
  }
}
